import {
    MESSAGE_EXTERNAL_UNAVAILABLE,
    MESSAGE_LOAD_MORE,
    MESSAGE_LOAD_MORE_LOADING,
    MESSAGE_TEMPORARY_ERROR,
} from './constants.js';

const placeholderIcons = ['🎮', '🕹️', '🃏', '📺', '🖥️', '🎯', '⚔️', '🏆'];
const placeholderGradients = ['1', '2', '3', '4', '5', '6'];

function getPlaceholderVariants(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
        hash = ((hash << 5) - hash) + seed.charCodeAt(i);
        hash = hash & hash;
    }
    const iconIndex = Math.abs(hash) % placeholderIcons.length;
    const gradientIndex = Math.abs(hash >> 8) % placeholderGradients.length;
    return { icon: placeholderIcons[iconIndex], gradient: placeholderGradients[gradientIndex] };
}

function renderCoverPlaceholder(coverUrl, seed) {
    if (coverUrl !== '') {
        return `<img src="${escapeHtml(coverUrl)}" alt="" loading="lazy" crossorigin="anonymous">`;
    }
    const { icon, gradient } = getPlaceholderVariants(seed);
    return `
        <div class="global-search-cover-placeholder ui-card-cover-placeholder ui-card-cover-placeholder--gradient-${gradient}">
            <span class="ui-card-cover-placeholder-icon">${icon}</span>
            <span>No cover</span>
        </div>
    `;
}

/**
 * `searchState`: `idle` (no query yet), `loading`, `ready`, `unavailable` (IGDB answered it cannot
 * search) or `error` (the request itself failed).
 */
export function renderResultsMarkup(state, options) {
    const {
        query = '',
        items = [],
        searchState = 'idle',
        nextOffset = null,
    } = state;

    const normalizedQuery = String(query).trim();
    const normalizedItems = Array.isArray(items) ? items : [];

    if (normalizedQuery === '' && searchState === 'idle') {
        return '';
    }

    const showLoadMore = searchState === 'ready' && Number.isInteger(nextOffset);
    const showResultsCount = searchState === 'ready' && normalizedItems.length > 0;

    let contentMarkup = '';
    if (searchState === 'loading') {
        contentMarkup = `<div class="global-search-results-grid">${renderLoadingSkeletonCards(6)}</div>`;
    } else if (searchState === 'unavailable' || searchState === 'error') {
        const message = searchState === 'unavailable' ? MESSAGE_EXTERNAL_UNAVAILABLE : MESSAGE_TEMPORARY_ERROR;
        contentMarkup = `<p class="global-search-results-note" data-state="error">${escapeHtml(message)}</p>`;
    } else if (searchState === 'ready' && normalizedItems.length === 0) {
        contentMarkup = `<p class="global-search-empty">No games found for "${escapeHtml(normalizedQuery)}".</p>`;
    } else if (searchState === 'ready') {
        contentMarkup = `<div class="global-search-results-grid">${renderAppendedResultCardsMarkup(normalizedItems, options)}</div>`;
    }

    const loadMoreMarkup = showLoadMore
        ? `<div class="global-search-load-more" data-global-search-load-more-slot>${renderLoadMoreButtonMarkup()}</div>`
        : '';

    return `
        <section class="global-search-results-section" data-search-state="${escapeHtml(searchState)}">
            <header class="global-search-results-header">
                <h3 class="global-search-results-title">Results</h3>
                ${showResultsCount ? `<span class="global-search-results-count">${escapeHtml(formatResultsCountLabel(normalizedItems.length))}</span>` : ''}
            </header>
            ${contentMarkup}
            ${loadMoreMarkup}
        </section>
    `;
}

export function formatResultsCountLabel(totalResults) {
    return totalResults === 1 ? '1 result' : `${totalResults} results`;
}

export function renderLoadMoreButtonMarkup() {
    return `<button type="button" class="btn btn-secondary btn-sm global-search-load-more-button" data-global-search-load-more>${escapeHtml(MESSAGE_LOAD_MORE)}</button>`;
}

export function syncLoadMoreButton(button, isLoading) {
    button.textContent = isLoading ? MESSAGE_LOAD_MORE_LOADING : MESSAGE_LOAD_MORE;
    if (isLoading) {
        button.dataset.loading = 'true';
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');

        return;
    }

    delete button.dataset.loading;
    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
}

export function renderAppendedResultCardsMarkup(items, options) {
    const normalizedItems = Array.isArray(items) ? items : [];

    return normalizedItems.map((item, index) => renderResultCard(item, {
        ...options,
        animateIn: true,
        animationDelay: `${Math.min(index, 5) * 30}ms`,
    })).join('');
}

function renderResultCard(item, options) {
    const {
        isManualListMode,
        buildDetailsRoute,
        buildAddRoute,
        isTogglePending,
        animateIn,
        animationDelay,
    } = options;

    const titleValue = typeof item.title === 'string' ? item.title : '';
    const badge = typeof item.badge === 'string' ? item.badge : '';
    const inLibrary = item.inLibrary === true;
    const coverUrl = typeof item.coverUrl === 'string' ? item.coverUrl : '';
    const source = typeof item.source === 'string' ? item.source : 'unknown';
    const igdbSourceKey = typeof item.igdbSourceKey === 'string' ? item.igdbSourceKey : '';
    const id = typeof item.id === 'string' ? item.id : '';
    const animationAttributes = animateIn === true
        ? ` data-animate-in="true" style="--global-search-card-delay: ${escapeHtml(animationDelay)};"`
        : '';

    const coverSeed = inLibrary && id !== '' ? id : (igdbSourceKey !== '' ? igdbSourceKey : titleValue);
    const cover = renderCoverPlaceholder(coverUrl, coverSeed);
    const sourceBadgeMarkup = inLibrary
        ? `<span class="global-search-badge global-search-badge-library">${escapeHtml(badge)}</span>`
        : '';
    const typeLabel = typeof item.typeLabel === 'string' ? item.typeLabel.trim() : '';
    const typeBadgeMarkup = typeLabel !== ''
        ? `<span class="global-search-badge global-search-badge-type">${escapeHtml(typeLabel)}</span>`
        : '';

    if (isManualListMode) {
        const gameId = id.trim();
        const sourceKey = igdbSourceKey.trim();
        const canToggleExisting = inLibrary && gameId !== '';
        const canCreateFromSearch = !inLibrary && sourceKey !== '' && titleValue !== '' && coverUrl !== '';
        const actionKey = canToggleExisting
            ? gameId
            : (canCreateFromSearch ? `igdb:${sourceKey}` : '');
        const isActionEnabled = actionKey !== '';
        const isOnManualList = item.isOnManualList === true;
        const actionKeyDataset = isActionEnabled
            ? ` data-global-search-manual-action-key="${escapeHtml(actionKey)}"`
            : '';
        const toggleDataset = canToggleExisting
            ? ` data-global-search-toggle-game-id="${escapeHtml(gameId)}"`
            : '';
        const createDataset = canCreateFromSearch
            ? ` data-global-search-add-igdb-source-key="${escapeHtml(sourceKey)}"`
                + ` data-global-search-add-title="${escapeHtml(titleValue)}"`
                + ` data-global-search-add-cover-url="${escapeHtml(coverUrl)}"`
            : '';
        const loading = isActionEnabled && isTogglePending(actionKey);
        const disabledAttribute = (!isActionEnabled || loading) ? ' disabled' : '';
        const loadingAttribute = loading ? ' data-loading="true"' : '';
        const listedCardClass = isOnManualList ? ' global-search-card-on-list' : '';
        const membershipBadgeMarkup = isOnManualList
            ? '<span class="global-search-badge global-search-badge-list-on">On list</span>'
            : '';

        return `
            <button
                type="button"
                class="global-search-card global-search-card-action ui-card-surface ui-game-card${listedCardClass}"
                data-source="${escapeHtml(source)}"${animationAttributes}${actionKeyDataset}${toggleDataset}${createDataset}${disabledAttribute}${loadingAttribute}
            >
                <div class="global-search-cover ui-card-cover ui-card-cover--portrait">${cover}</div>
                <div class="global-search-card-body ui-card-body">
                    <h3 class="global-search-card-title ui-card-title">${escapeHtml(titleValue)}</h3>
                    <div class="global-search-badges ui-card-badges">
                        ${sourceBadgeMarkup}
                        ${typeBadgeMarkup}
                        ${membershipBadgeMarkup}
                    </div>
                </div>
            </button>
        `;
    }

    let href = '#';
    if (inLibrary && id !== '') {
        const detailsRoute = buildDetailsRoute(id);
        if (typeof detailsRoute === 'string') {
            href = detailsRoute;
        }
    } else {
        const addRoute = buildAddRoute({
            igdb_source_key: igdbSourceKey,
            title: titleValue,
            cover_url: coverUrl,
        });

        if (typeof addRoute === 'string') {
            href = addRoute;
        }
    }

    const badgeMarkup = sourceBadgeMarkup !== '' || typeBadgeMarkup !== ''
        ? `<div class="global-search-badges ui-card-badges">${sourceBadgeMarkup}${typeBadgeMarkup}</div>`
        : '';

    return `
        <a class="global-search-card ui-card-surface ui-game-card" data-source="${escapeHtml(source)}"${animationAttributes} href="${escapeHtml(href)}">
            <div class="global-search-cover ui-card-cover ui-card-cover--portrait">${cover}</div>
            <div class="global-search-card-body ui-card-body">
                <h3 class="global-search-card-title ui-card-title">${escapeHtml(titleValue)}</h3>
                ${badgeMarkup}
            </div>
        </a>
    `;
}

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function renderLoadingSkeletonCards(count) {
    return Array.from({ length: count }, () => `
        <div class="global-search-card global-search-card-skeleton ui-card-surface ui-game-card" aria-hidden="true">
            <div class="global-search-cover ui-card-cover ui-card-cover--portrait">
                <div class="global-search-skeleton-block global-search-skeleton-cover"></div>
            </div>
            <div class="global-search-card-body ui-card-body">
                <div class="global-search-skeleton-block global-search-skeleton-title"></div>
            </div>
        </div>
    `).join('');
}
